import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Database } from "lucide-react";
import { EmptyState } from "./EmptyState";
import { QueryErrorCard } from "@/components/QueryErrorCard";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { Skeleton } from "@/components/ui/Skeleton";
import { Tooltip } from "@/components/ui/Tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/Table";
import { useNow } from "@/components/ClockProvider";
import { useKeysetQuery } from "@/hooks/useKeyset";
import { qs } from "@/lib/api";
import { S } from "@/lib/strings";
import { timeAgo, truncate } from "@/lib/utils";
import type { ExtractionRow } from "@/types/fleet";

const PAGE_SIZE = 50;
/** Generated columns shown inline. The rest of a record is one click away in the dialog. */
const MAX_DATA_COLUMNS = 5;

export interface ExtractionFilter {
  task_id?: string;
  device_id?: string;
  run_id?: string;
  q?: string;
  since?: string;
}

/** A cell value that is not a string: nested objects and lists are common in a free-form schema. */
function cellText(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return JSON.stringify(value);
}

/**
 * Columns are generated from the data, not declared.
 *
 * A task without an extraction schema produces whatever the model decided to record, and two
 * tasks in the same fleet never produce the same fields. Insertion order across the loaded rows
 * keeps the columns stable while paging: a key seen on row 1 does not jump when row 80 arrives.
 */
export function dataColumns(rows: ExtractionRow[], max = MAX_DATA_COLUMNS): string[] {
  const seen = new Set<string>();
  for (const row of rows) {
    for (const key of Object.keys(row.data ?? {})) seen.add(key);
  }
  return [...seen].slice(0, max);
}

export function ExtractionTable({
  filter,
  hideTaskColumn = false,
  emptyBody,
}: {
  filter: ExtractionFilter;
  hideTaskColumn?: boolean;
  emptyBody?: string;
}) {
  const now = useNow();
  const [open, setOpen] = useState<ExtractionRow | null>(null);

  const query = useKeysetQuery<ExtractionRow>(
    ["extractions", "list", filter],
    (cursor) =>
      `/extractions${qs({
        task_id: filter.task_id,
        device_id: filter.device_id,
        run_id: filter.run_id,
        q: filter.q,
        since: filter.since,
        limit: PAGE_SIZE,
        cursor,
      })}`,
  );

  const rows = query.items;
  const columns = useMemo(() => dataColumns(rows), [rows]);

  if (query.isError) {
    return <QueryErrorCard error={query.error} onRetry={() => void query.refetch()} />;
  }

  if (query.isInitialLoading) {
    return (
      <div className="space-y-1.5">
        {Array.from({ length: 6 }, (_, i) => (
          <Skeleton key={i} className="h-11 w-full" />
        ))}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <EmptyState
        icon={Database}
        title={S.noDataFiltered}
        body={emptyBody ?? S.noDataBody}
        compact
      />
    );
  }

  return (
    <>
      {/* The generated columns make this table as wide as the data; it scrolls on its own
          rather than pushing the page sideways. */}
      <div className="overflow-x-auto">
        <Table>
          <TableHead>
            <tr>
              {!hideTaskColumn && <TableHeaderCell className="w-40">{S.colTask}</TableHeaderCell>}
              {columns.map((c) => (
                <TableHeaderCell key={c}>{c}</TableHeaderCell>
              ))}
              <TableHeaderCell className="w-24">{S.colCreated}</TableHeaderCell>
            </tr>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const extra = Object.keys(row.data ?? {}).length - columns.length;
              return (
                <TableRow key={row.id} onClick={() => setOpen(row)} className="cursor-pointer">
                  {!hideTaskColumn && (
                    <TableCell className="text-[var(--color-fg-muted)]">
                      {truncate(row.task_name ?? row.task_id ?? "—", 28)}
                    </TableCell>
                  )}
                  {columns.map((c) => (
                    <TableCell key={c} className="max-w-[26rem]">
                      <span className="block truncate">{truncate(cellText(row.data?.[c]), 160)}</span>
                    </TableCell>
                  ))}
                  <TableCell className="whitespace-nowrap text-[var(--color-fg-subtle)]">
                    <Tooltip content={new Date(row.created_at).toLocaleString()}>
                      <span>{timeAgo(new Date(row.created_at).getTime(), now)}</span>
                    </Tooltip>
                    {extra > 0 && (
                      <span className="ml-1.5 text-[11px] text-[var(--color-fg-faint)]">
                        +{extra} {S.fieldsHidden}
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {query.canLoadMore && (
        <div className="mt-3 flex justify-center">
          <Button
            size="sm"
            variant="ghost"
            onClick={query.loadMore}
            disabled={query.isFetchingNextPage}
          >
            {query.isFetchingNextPage ? S.loading : S.loadMore}
          </Button>
        </div>
      )}

      <Dialog
        open={open !== null}
        onOpenChange={(v) => !v && setOpen(null)}
        title={S.recordDetail}
        description={open?.task_name ?? undefined}
        size="lg"
        footer={
          open ? (
            <div className="flex w-full items-center justify-between gap-2">
              <Link
                to={`/runs/${open.run_id}`}
                className="text-[13px] text-[var(--color-accent)] hover:underline"
              >
                {S.viewRun}
              </Link>
              <Button size="sm" variant="ghost" onClick={() => setOpen(null)}>
                {S.close}
              </Button>
            </div>
          ) : undefined
        }
      >
        {open && (
          <div className="space-y-3">
            <dl className="grid grid-cols-[8rem_1fr] gap-x-3 gap-y-1.5 text-[13px]">
              {Object.entries(open.data ?? {}).map(([k, v]) => (
                <div key={k} className="contents">
                  <dt className="truncate font-mono text-[11px] text-[var(--color-fg-subtle)]">
                    {k}
                  </dt>
                  <dd className="whitespace-pre-wrap break-words text-[var(--color-fg-muted)]">
                    {cellText(v)}
                  </dd>
                </div>
              ))}
            </dl>
            <pre className="max-h-64 overflow-auto rounded-md border border-[var(--color-border-subtle)] bg-[rgba(255,255,255,0.02)] p-2.5 font-mono text-[11px] leading-relaxed text-[var(--color-fg-subtle)]">
              {JSON.stringify(open.data ?? {}, null, 2)}
            </pre>
          </div>
        )}
      </Dialog>
    </>
  );
}
